import axios from 'axios';
import { Sentry } from './sentry';
import { getAuthState } from '@/contexts/AuthContext';
import { cryptoPriceApiService } from '@/services/cryptoPriceApiService';
import { getFreshCachedPrice, setCachedPrice } from './priceCache';
import { COINS_MARKETS_CHUNK_SIZE } from './constants';

const COINGECKO_BASE_URL = 'https://api.coingecko.com/api/v3';
const LIVECOINWATCH_BASE_URL = 'https://api.livecoinwatch.com';

// Clé CoinGecko Demo (gratuite, https://www.coingecko.com/en/developers/dashboard) — relève la
// limite de ~10-30 appels/min (tier anonyme) à 100 appels/min. Optionnelle : en son absence,
// on reste sur le comportement anonyme précédent.
const COINGECKO_API_KEY = import.meta.env.VITE_COINGECKO_API_KEY as string | undefined;
const COINGECKO_HEADERS = COINGECKO_API_KEY ? { 'x-cg-demo-api-key': COINGECKO_API_KEY } : undefined;

// Configuration pour LiveCoinWatch — clé via VITE_LIVECOINWATCH_API_KEY (compte gratuit sur livecoinwatch.com)
const LIVECOINWATCH_API_KEY = import.meta.env.VITE_LIVECOINWATCH_API_KEY as string | undefined;
const LIVECOINWATCH_AVAILABLE = !!LIVECOINWATCH_API_KEY;

const LIVECOINWATCH_CONFIG = {
  headers: {
    'content-type': 'application/json',
    'x-api-key': LIVECOINWATCH_API_KEY ?? ''
  }
};

// Fonction pour attendre entre les requêtes
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export type CoinGeckoRequestPriority = 'interactive' | 'background';

interface QueueEntry {
  run: () => Promise<void>;
  /** 1 = interactive (saisie utilisateur), 0 = background (backfill portefeuille) */
  priority: number;
  /** ordre d'arrivée, pour rester FIFO à priorité égale */
  seq: number;
}

// File d'attente globale pour les appels CoinGecko : toutes les requêtes de l'app (calcul
// du portefeuille, prix d'une transaction, recherche) passent par une seule file séquentielle
// (concurrence 1) au lieu de partir en parallèle depuis des hooks différents, ce qui est ce qui
// déclenchait des rafales de 429 sur le tier gratuit. L'espacement s'élargit automatiquement
// (backOff) dès qu'un 429 est rencontré, pour toute requête suivante, pas juste celle en échec.
//
// Deux niveaux de priorité : une requête `interactive` (typeahead de recherche crypto) passe
// devant les requêtes `background` déjà en file (backfill de la case 212, qui peut aligner des
// dizaines de tokens). Sans ça, taper « BTC » dans le champ crypto d'une vente reste bloqué
// derrière tout le calcul de valeur du portefeuille. La concurrence reste à 1 : la cadence
// anti-429 est identique, seul l'ordre change.
export class CoinGeckoRateLimiter {
  private queue: QueueEntry[] = [];
  private running = false;
  private seq = 0;
  // Avec clé Demo : 1200ms, plus prudent que le débit moyen annoncé (100/min) car Cloudflare
  // applique en plus un rate-limit de rafale à l'IP, plus strict, indépendant du quota de la
  // clé. Sans clé (tier anonyme) : 2500ms.
  private intervalMs: number;

  constructor(intervalMs = COINGECKO_API_KEY ? 1200 : 2500) {
    this.intervalMs = intervalMs;
  }

  schedule<T>(
    fn: () => Promise<T>,
    opts: { priority?: CoinGeckoRequestPriority } = {}
  ): Promise<T> {
    const priority = opts.priority === 'interactive' ? 1 : 0;
    return new Promise<T>((resolve, reject) => {
      const seq = this.seq++;
      const entry: QueueEntry = {
        priority,
        seq,
        run: async () => {
          try {
            resolve(await fn());
          } catch (err) {
            reject(err);
          }
        },
      };
      // Insérer devant la première entrée de priorité strictement inférieure ; sinon à la fin.
      // Comme `seq` est croissant, on reste FIFO au sein d'une même priorité.
      const idx = this.queue.findIndex((e) => e.priority < priority);
      if (idx === -1) {
        this.queue.push(entry);
      } else {
        this.queue.splice(idx, 0, entry);
      }
      this.run();
    });
  }

  // Élargit l'espacement entre requêtes (jusqu'à 20s) après un 429
  backOff() {
    this.intervalMs = Math.min(this.intervalMs * 2, 20000);
  }

  get currentIntervalMs() {
    return this.intervalMs;
  }

  private async run() {
    if (this.running) return;
    this.running = true;
    while (this.queue.length > 0) {
      const entry = this.queue.shift()!;
      await entry.run();
      if (this.queue.length > 0) {
        await delay(this.intervalMs);
      }
    }
    this.running = false;
  }
}

const coinGeckoLimiter = new CoinGeckoRateLimiter();

// Un throttle en périphérie (Cloudflare) devant CoinGecko peut bloquer une requête avant même
// qu'elle atteigne le backend qui renvoie le vrai 429 — dans ce cas la réponse n'a pas les en-têtes
// CORS, donc le navigateur ne peut pas exposer son status code à JS : Axios remonte une erreur
// réseau opaque (`error.response` est `undefined`), affichée dans la console comme une "erreur
// CORS" alors qu'il s'agit d'un rate-limit déguisé. On la traite comme un 429 pour déclencher le
// même backoff, plutôt que d'abandonner immédiatement sans ralentir la cadence.
const isRetryableCoinGeckoError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;
  if (error.response?.status === 429) return true;
  return error.response === undefined && error.code !== 'ECONNABORTED';
};

// Exécute une requête GET CoinGecko via la file globale, avec retry + backoff sur 429
// (respecte l'en-tête Retry-After si présent), au lieu d'abandonner au premier throttle.
const requestCoinGecko = async <T = unknown>(
  url: string,
  params: Record<string, unknown>,
  timeout: number,
  maxAttempts = 3,
  priority: CoinGeckoRequestPriority = 'background'
): Promise<{ data: T }> => {
  return coinGeckoLimiter.schedule(async () => {
    let lastError: unknown;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        return await axios.get<T>(url, { params, timeout, headers: COINGECKO_HEADERS });
      } catch (error) {
        lastError = error;
        const isLastAttempt = attempt === maxAttempts - 1;
        if (isRetryableCoinGeckoError(error) && !isLastAttempt) {
          coinGeckoLimiter.backOff();
          const retryAfterHeader = axios.isAxiosError(error) ? error.response?.headers?.['retry-after'] : undefined;
          const waitMs = retryAfterHeader ? Number(retryAfterHeader) * 1000 : coinGeckoLimiter.currentIntervalMs;
          await delay(waitMs);
          continue;
        }
        throw error;
      }
    }
    throw lastError;
  }, { priority });
};

// Retourne true si l'erreur LCW est irrécupérable (pas de clé, quota dépassé)
const isLiveCoinWatchUnavailable = (error: unknown): boolean => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    return status === 401 || status === 403;
  }
  return false;
};

// Mapping des symboles vers les IDs CoinGecko pour les cryptos populaires
const SYMBOL_TO_COINGECKO_ID: Record<string, string> = {
  'ERG': 'ergo',
  'BTC': 'bitcoin',
  'ETH': 'ethereum',
  'ADA': 'cardano',
  'DOT': 'polkadot',
  'LINK': 'chainlink',
  'LTC': 'litecoin',
  'XMR': 'monero',
  'DOGE': 'dogecoin',
  'MATIC': 'matic-network',
  'AVAX': 'avalanche-2',
  'ATOM': 'cosmos',
  'SOL': 'solana',
  'ALGO': 'algorand',
  'XLM': 'stellar',
  'XRP': 'ripple'
};

// Mapping inverse : CoinGecko ID → symbol (pour conversion lors du fallback)
const COINGECKO_ID_TO_SYMBOL: Record<string, string> = Object.fromEntries(
  Object.entries(SYMBOL_TO_COINGECKO_ID).map(([symbol, id]) => [id, symbol])
);

// Extrait le symbol ticker depuis un CoinGecko ID
export const getSymbolFromCoinId = (coinId: string): string => {
  if (COINGECKO_ID_TO_SYMBOL[coinId]) return COINGECKO_ID_TO_SYMBOL[coinId];

  // CoinGecko désambiguïse les IDs en collision de ticker avec un suffixe "-<n>"
  // (ex: "pearl-2"). Ce suffixe ne fait pas partie du vrai ticker utilisé par les
  // autres fournisseurs de prix (LiveCoinWatch), donc il faut le retirer avant de
  // dériver le symbole — sinon "pearl-2" est envoyé tel quel à LiveCoinWatch, qui
  // ne connaît pas ce code et répond "coin non trouvé".
  const withoutDisambiguator = coinId.replace(/-\d+$/, '');

  return withoutDisambiguator.length <= 10
    ? withoutDisambiguator.toUpperCase()
    : (withoutDisambiguator.split('-')[0]?.toUpperCase() ?? withoutDisambiguator.toUpperCase());
};

// Fonction pour convertir un ID/symbol LiveCoinWatch vers un ID CoinGecko
const convertToCoingeckoId = (liveCoinWatchId: string, symbol: string): string => {
  // Si on a un mapping direct via le symbol
  if (SYMBOL_TO_COINGECKO_ID[symbol.toUpperCase()]) {
    return SYMBOL_TO_COINGECKO_ID[symbol.toUpperCase()];
  }

  // Sinon, on essaie l'ID tel quel (fonctionne souvent)
  return liveCoinWatchId.toLowerCase();
};


// Recherche un coin par ticker exact via LiveCoinWatch (fallback quand CoinGecko retourne vide)
const searchCryptocurrencyLiveCoinWatch = async (query: string): Promise<CryptoCurrency[]> => {
  if (!LIVECOINWATCH_AVAILABLE) {
    console.warn('LiveCoinWatch non disponible pour la recherche (VITE_LIVECOINWATCH_API_KEY non configurée)');
    return [];
  }

  try {
    const response = await axios.post(
      `${LIVECOINWATCH_BASE_URL}/coins/single`,
      {
        currency: 'EUR',
        code: query.toUpperCase(),
        meta: true
      },
      {
        ...LIVECOINWATCH_CONFIG,
        timeout: 10000
      }
    );

    // LCW /coins/single ne retourne pas le champ `code` — on utilise le query
    const coin = response.data as { name?: string; rate?: number } | null;
    if (!coin || coin.rate === undefined) return [];

    const code = query.toUpperCase();
    return [{
      id: convertToCoingeckoId(code.toLowerCase(), code),
      symbol: code,
      name: coin.name || code,
      source: 'livecoinwatch' as const,
    }];
  } catch (error) {
    if (isLiveCoinWatchUnavailable(error)) {
      console.warn('LiveCoinWatch: clé API manquante ou invalide (401/403)');
    } else if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const data = error.response?.data;
      
      // LiveCoinWatch retourne 400 avec "Unknown coin code" pour les coins inexistants
      // Ce n'est pas une vraie erreur, juste un coin non trouvé
      if (status === 400 && data?.error?.description === 'Unknown coin code.') {
        console.debug(`LiveCoinWatch: coin "${query}" non trouvé (400 - Unknown coin code)`);
        // Ne pas remonter à Sentry, c'est un comportement normal
        return [];
      }
      
      // Coin inconnu de LCW — pas d'erreur à remonter
      if (status === 404) {
        return [];
      }
    }
    
    console.error('Erreur LiveCoinWatch /coins/single (search):', error);
    Sentry.captureException(error, {
      tags: { source: 'livecoinwatch', operation: 'searchSingle' },
      extra: { query }
    });
    return [];
  }
};

// Fonction pour obtenir le prix actuel via LiveCoinWatch
const getCurrentCryptoPriceLiveCoinWatch = async (symbol: string): Promise<number | null> => {
  if (!LIVECOINWATCH_AVAILABLE) return null;

  try {
    const response = await axios.post(
      `${LIVECOINWATCH_BASE_URL}/coins/single`,
      {
        currency: 'EUR',
        code: symbol.toUpperCase(),
        meta: false
      },
      {
        ...LIVECOINWATCH_CONFIG,
        timeout: 10000
      }
    );

    return response.data.rate || null;
  } catch (error) {
    if (isLiveCoinWatchUnavailable(error)) {
      console.warn('LiveCoinWatch: clé API manquante ou invalide (401/403), fallback désactivé');
    } else {
      console.error('Erreur lors de la récupération du prix via LiveCoinWatch:', error);
      Sentry.captureException(error, {
        tags: { source: 'livecoinwatch', operation: 'getCurrentPrice' },
        extra: { symbol }
      });
    }
    return null;
  }
};

// Fonction pour obtenir le prix historique via LiveCoinWatch (fallback)
const getHistoricalPriceLiveCoinWatch = async (symbol: string, date: Date): Promise<number | null> => {
  if (!LIVECOINWATCH_AVAILABLE) return null;

  try {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    const response = await axios.post(
      `${LIVECOINWATCH_BASE_URL}/coins/single/history`,
      {
        currency: 'EUR',
        code: symbol.toUpperCase(),
        start: startOfDay.getTime(),
        end: endOfDay.getTime(),
        meta: false
      },
      {
        ...LIVECOINWATCH_CONFIG,
        timeout: 10000
      }
    );

    const history = response.data.history as Array<{ date: number; rate: number }> | undefined;
    if (history && history.length > 0) {
      // Prendre le prix le plus proche de la date cible (dernier point du jour)
      return history[history.length - 1].rate;
    }
    return null;
  } catch (error) {
    if (isLiveCoinWatchUnavailable(error)) {
      console.warn('LiveCoinWatch: clé API manquante ou invalide (401/403), fallback désactivé');
    } else {
      console.error('Erreur lors de la récupération du prix historique via LiveCoinWatch:', error);
      Sentry.captureException(error, {
        tags: { source: 'livecoinwatch', operation: 'getHistoricalPrice' },
        extra: { symbol, date: date.toISOString() }
      });
    }
    return null;
  }
};

export interface CryptoCurrency {
  id: string;
  symbol: string;
  name: string;
  source?: 'coingecko' | 'livecoinwatch'; // Indiquer la source de données
}

export interface PriceData {
  price: number;
  timestamp: number;
}

export const searchCryptocurrencies = async (query: string): Promise<CryptoCurrency[]> => {
  try {
    if (!query || query.length < 2) return [];

    // Essayer d'abord CoinGecko
    try {
      interface CoinGeckoCoin { id: string; symbol: string; name: string; }
      // Priorité `interactive` : ce typeahead doit passer devant le backfill de la case 212
      // (requêtes `background`) déjà en file, sinon la recherche crypto reste bloquée pendant
      // tout le calcul de valeur du portefeuille.
      const response = await requestCoinGecko<{ coins: CoinGeckoCoin[] }>(
        `${COINGECKO_BASE_URL}/search`,
        { query: query.toLowerCase() },
        10000,
        3,
        'interactive'
      );

      const results = response.data.coins.slice(0, 10).map((coin) => ({
        id: coin.id,
        symbol: coin.symbol.toUpperCase(),
        name: coin.name,
        source: 'coingecko' as const,
      }));

      const queryLower = query.toLowerCase();
      const queryUpper = query.toUpperCase();

      const sortedResults = results.sort((a, b) => {
        if (a.symbol === queryUpper && b.symbol !== queryUpper) return -1;
        if (b.symbol === queryUpper && a.symbol !== queryUpper) return 1;
        if (a.name.toLowerCase() === queryLower && b.name.toLowerCase() !== queryLower) return -1;
        if (b.name.toLowerCase() === queryLower && a.name.toLowerCase() !== queryLower) return 1;
        if (a.symbol.startsWith(queryUpper) && !b.symbol.startsWith(queryUpper)) return -1;
        if (b.symbol.startsWith(queryUpper) && !a.symbol.startsWith(queryUpper)) return 1;
        if (a.name.toLowerCase().startsWith(queryLower) && !b.name.toLowerCase().startsWith(queryLower)) return -1;
        if (b.name.toLowerCase().startsWith(queryLower) && !a.name.toLowerCase().startsWith(queryLower)) return 1;
        return 0;
      });

      // CoinGecko vide → tentative LCW par ticker exact
      if (sortedResults.length === 0) {
        await delay(200);
        const lcwResult = await searchCryptocurrencyLiveCoinWatch(query);
        if (lcwResult.length > 0) {
          console.log(`LiveCoinWatch: coin trouvé pour ticker "${query.toUpperCase()}"`);
          return lcwResult;
        }
      }

      return sortedResults;

    } catch (coinGeckoError: unknown) {
      if (axios.isAxiosError(coinGeckoError)) {
        console.warn('Erreur CoinGecko search:', coinGeckoError.response?.status, coinGeckoError.message);
      } else {
        console.warn('Erreur CoinGecko search:', (coinGeckoError as Error).message);
      }

      // CoinGecko KO → tentative LCW par ticker exact
      await delay(200);
      const lcwResult = await searchCryptocurrencyLiveCoinWatch(query);
      if (lcwResult.length > 0) return lcwResult;

      throw coinGeckoError;
    }

  } catch (error) {
    console.error('Erreur lors de la recherche de cryptomonnaies (tous les services):', error);
    Sentry.captureException(error, {
      tags: { source: 'coingecko', operation: 'search' },
      extra: { query }
    });
    return [];
  }
};

export const getCryptoPriceAtDate = async (
  coinId: string,
  date: Date
): Promise<number | null> => {
  if (coinId.startsWith('custom-')) return null;

  // Mode authentifié : le prix historique (immuable) est d'abord cherché dans le cache serveur
  // partagé entre tous les utilisateurs, ce qui évite de retélécharger indéfiniment le même prix
  // depuis CoinGecko à chaque session. Un `null` (miss + échec de récupération côté serveur) ou
  // une erreur réseau vers le backend ne doit jamais bloquer la fonctionnalité : on retombe sur
  // la logique CoinGecko/LiveCoinWatch existante ci-dessous, seul chemin utilisé en mode invité.
  const { isAuthenticated } = getAuthState();
  if (isAuthenticated) {
    try {
      const isoDate = date.toISOString().split('T')[0];
      const cachedPrice = await cryptoPriceApiService.getHistoricalPrice(coinId, isoDate);
      if (cachedPrice !== null) return cachedPrice;
    } catch (error) {
      Sentry.captureException(error, {
        tags: { source: 'crypto-price-cache', operation: 'getCryptoPriceAtDate_api' },
        level: 'warning',
        extra: { coinId, date: date.toISOString() },
      });
    }
  }

  try {
    const formattedDate = date.toISOString().split('T')[0].split('-').reverse().join('-');

    try {
      const response = await requestCoinGecko<{ market_data?: { current_price?: { eur?: number } } }>(
        `${COINGECKO_BASE_URL}/coins/${coinId}/history`,
        { date: formattedDate, localization: false },
        15000
      );

      const price = response.data.market_data?.current_price?.eur || null;

      // CoinGecko répond 200 mais sans données de marché (coin inconnu ou pas de données à cette date)
      if (price === null) {
        const symbol = getSymbolFromCoinId(coinId);
        await delay(200);
        const liveCoinWatchPrice = await getHistoricalPriceLiveCoinWatch(symbol, date);
        if (liveCoinWatchPrice !== null) {
          return liveCoinWatchPrice;
        }
      }

      return price;

    } catch (coinGeckoError: unknown) {
      if (axios.isAxiosError(coinGeckoError)) {
        const status = coinGeckoError.response?.status;
        if (status === 429) {
          console.warn('API CoinGecko throttlée pour les prix historiques, veuillez patienter...');
        } else if (status === 404) {
          console.warn(`Crypto ${coinId} non trouvée dans CoinGecko (404), tentative avec LiveCoinWatch...`);
        } else {
          Sentry.captureException(coinGeckoError, {
            tags: { source: 'coingecko', operation: 'getPriceAtDate' },
            extra: { coinId, date: date.toISOString() }
          });
        }

        // Fallback LiveCoinWatch sur 404 ou 429 ou 5xx
        if (status === 404 || status === 429 || (status !== undefined && status >= 500)) {
          const symbol = getSymbolFromCoinId(coinId);
          await delay(200);
          const liveCoinWatchPrice = await getHistoricalPriceLiveCoinWatch(symbol, date);
          if (liveCoinWatchPrice !== null) {
            console.log(`Prix historique récupéré via LiveCoinWatch pour ${coinId} (${symbol})`);
            return liveCoinWatchPrice;
          }
        }
      }

      throw coinGeckoError;
    }

  } catch (error) {
    console.error(`Erreur lors de la récupération du prix historique pour ${coinId} (tous les services):`, error);
    return null;
  }
};

export const getCurrentCryptoPrice = async (coinId: string): Promise<number | null> => {
  if (coinId.startsWith('custom-')) return null;

  // Prix encore frais (< PRICE_CACHE_TTL_MS) en cache local : on évite la file CoinGecko
  // (concurrence 1) entièrement, ce qui accélère fortement l'affichage d'un portefeuille
  // avec beaucoup de tokens.
  const cachedPrice = getFreshCachedPrice(coinId);
  if (cachedPrice !== null) return cachedPrice;

  const price = await fetchCurrentCryptoPrice(coinId);
  if (price !== null) setCachedPrice(coinId, price);
  return price;
};

const fetchCurrentCryptoPrice = async (coinId: string): Promise<number | null> => {
  try {
    // Essayer d'abord CoinGecko
    try {
      const response = await requestCoinGecko<Record<string, { eur?: number }>>(
        `${COINGECKO_BASE_URL}/simple/price`,
        { ids: coinId, vs_currencies: 'eur' },
        10000
      );

      const price = response.data[coinId]?.eur || null;

      // CoinGecko répond 200 mais ne connaît pas ce coin (réponse vide `{}`)
      // → tenter LCW directement sans attendre une erreur HTTP
      if (price === null) {
        const symbol = getSymbolFromCoinId(coinId);
        await delay(200);
        const liveCoinWatchPrice = await getCurrentCryptoPriceLiveCoinWatch(symbol);
        if (liveCoinWatchPrice !== null) {
          return liveCoinWatchPrice;
        }
      }

      return price;

    } catch (coinGeckoError: unknown) {
      if (axios.isAxiosError(coinGeckoError)) {
        const status = coinGeckoError.response?.status;
        // Fallback LiveCoinWatch sur 404, 429 ou 5xx
        if (status === 404 || status === 429 || (status !== undefined && status >= 500)) {
          console.warn(`CoinGecko indisponible pour le prix (${status ?? 'réseau'}), tentative avec LiveCoinWatch...`);

          const symbol = getSymbolFromCoinId(coinId);
          await delay(200);
          const liveCoinWatchPrice = await getCurrentCryptoPriceLiveCoinWatch(symbol);

          if (liveCoinWatchPrice !== null) {
            return liveCoinWatchPrice;
          }
        }
      }

      throw coinGeckoError;
    }

  } catch (error) {
    console.error('Erreur lors de la récupération du prix actuel (tous les services):', error);
    Sentry.captureException(error, {
      tags: { source: 'coingecko', operation: 'getCurrentPrice' },
      extra: { coinId }
    });
    return null;
  }
};

const chunkArray = <T,>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

/**
 * Récupère le prix courant de plusieurs coins en un seul appel groupé CoinGecko
 * (`/coins/markets`), au lieu d'un appel `/simple/price` par coin via la file séquentielle
 * (concurrence 1) — élimine l'essentiel de la latence du premier chargement d'un portefeuille
 * à plusieurs tokens (Répartition du Portfolio, Mes tokens). Les coins déjà frais dans le cache
 * local (`priceCache.ts`) ne sont pas re-fetchés.
 *
 * Un coin absent du résultat (inconnu de CoinGecko, ou chunk en échec) vaut `null` dans le
 * résultat — à l'appelant de retomber sur `getCurrentCryptoPrice` (avec son fallback
 * LiveCoinWatch) pour ces cas individuels plutôt que d'abandonner silencieusement.
 */
export const getCurrentCryptoPrices = async (
  coinIds: string[]
): Promise<Record<string, number | null>> => {
  const uniqueIds = Array.from(new Set(coinIds.filter((id) => !id.startsWith('custom-'))));
  const result: Record<string, number | null> = {};

  const idsToFetch: string[] = [];
  for (const id of uniqueIds) {
    const cachedPrice = getFreshCachedPrice(id);
    if (cachedPrice !== null) {
      result[id] = cachedPrice;
    } else {
      idsToFetch.push(id);
    }
  }

  if (idsToFetch.length === 0) return result;

  const chunks = chunkArray(idsToFetch, COINS_MARKETS_CHUNK_SIZE);

  for (const chunk of chunks) {
    try {
      const response = await requestCoinGecko<Array<{ id: string; current_price: number | null }>>(
        `${COINGECKO_BASE_URL}/coins/markets`,
        { ids: chunk.join(','), vs_currency: 'eur' },
        15000
      );

      const priceById = new Map(response.data.map((entry) => [entry.id, entry.current_price]));

      for (const id of chunk) {
        const price = priceById.get(id) ?? null;
        if (price !== null) {
          setCachedPrice(id, price);
        }
        result[id] = price;
      }
    } catch (error) {
      console.error('Erreur lors de la récupération groupée des prix (coins/markets):', error);
      Sentry.captureException(error, {
        tags: { source: 'coingecko', operation: 'getCurrentPricesBulk' },
        extra: { chunk }
      });
      for (const id of chunk) {
        result[id] = null;
      }
    }
  }

  return result;
};